/**
 * Currency exchange: an ordinary dropdown with "Currency exchange" ticked in
 * its Data tab (`appearance.currencyExchange`). Its selected value is a rate:
 * instead of filtering its targets, every target is shown × that rate (tables:
 * the columns ticked "Convert currency"). Typically View in (currency) targets
 * it, so picking GBP refreshes it to GBP's rate, which is selected at once.
 */
import type { DisplayCurrency } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import type { DesignNode } from '../design-builder.types';

export type { DisplayCurrency };

/** Whether the node is a dropdown with "Currency exchange" ticked. */
export function isCurrencyExchange(node: DesignNode | null | undefined): boolean {
  return node?.type === 'WIDGET' && node.widget?.layoutType === 'DROPDOWN' && node.widget.appearance?.['currencyExchange'] === true;
}

/** Whether the node is a dropdown with "Show the value only" ticked: its pick filters / refreshes nothing. */
export function isValueOnlyDropdown(node: DesignNode | null | undefined): boolean {
  return node?.type === 'WIDGET' && node.widget?.layoutType === 'DROPDOWN' && node.widget.appearance?.['valueOnly'] === true;
}

const SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', JPY: '¥', CNY: '¥', RMB: '¥', KRW: '₩', RUB: '₽', BRL: 'R$', MXN: 'MX$',
  ZAR: 'R', GHS: 'GH₵', NGN: '₦', AUD: 'A$', CAD: 'C$', CHF: 'CHF ', SEK: 'kr ', NOK: 'kr ', DKK: 'kr ', TRY: '₺',
  IDR: 'Rp ', THB: '฿', PHP: '₱', VND: '₫', AED: 'AED ', SAR: 'SAR ', PLN: 'zł ', CLP: 'CLP$', ARS: 'AR$', PEN: 'S/ ',
  COP: 'COL$', SGD: 'S$', HKD: 'HK$', NZD: 'NZ$', MYR: 'RM ', EGP: 'E£', KES: 'KSh ', ILS: '₪', CZK: 'Kč ', HUF: 'Ft ',
};

/** The symbol shown in front of a converted value: £ for GBP, else the code itself ("XYZ "). */
export function currencySymbol(code: string): string {
  const key = code.trim().toUpperCase();
  return SYMBOLS[key] ?? (key ? `${key} ` : '');
}

/** value × rate, or the value as is when nothing converts it. */
export function convertCurrencyValue(value: number | string | null, currency: DisplayCurrency | null | undefined): number | string | null {
  if (!currency || value === null || value === '') return value;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric * currency.rate : value;
}
